import React, { ReactNode, Fragment } from 'react'
import { WithTooltip } from '../../design-system'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled'
import { FormattedMessage } from 'react-intl'
import { getSensitiveDataRoute } from '../route'
import { Link } from '@contrast-security-inc/design-system-typography'

export const maskedTooltipBodyTestID = 'sensitive-data-masked-tooltip-data'
const redactedPrefix = 'contrast-redacted-'

/* This regular expression is based on the assumption that the
redacted string will be replaced with `contrast-redacted-${keyword}`*/
const redactedRegex = /(contrast-redacted-[A-Za-z0-9\-\_]*)/g

interface Props {
  readonly text: string
}

const tooltipLinkClass = css`
  color: ${theme.color.turquoise50} !important;
  margin-left: 5px;
`

export function replaceTrailingAndLeadingBlankSpaces(string: string) {
  const trailingAndLeadingSpacesRegex = /^[ ]+|[ ]+$/g
  return string.replace(trailingAndLeadingSpacesRegex, '\u00a0')
}

export function WithRedactedTooltip({ text }: Props) {
  const elements = getSplitItems(text)

  return (
    <Fragment>
      {elements.map((string, index) => {
        string = replaceTrailingAndLeadingBlankSpaces(string)

        if (string.includes(redactedPrefix)) {
          return <WithRedactedTooltipSingle string={string} key={index} />
        }

        return (
          <span
            className={css`
              white-space: pre-wrap;
              word-break: break-word;
            `}
            key={index}
          >
            {string}
          </span>
        )
      })}
    </Fragment>
  )
}

export function WithRedactedTooltipSingle({
  string
}: {
  readonly string: string
}) {
  return (
    <WithTooltip
      popperContainerClassname={css`
        display: inline-block;
      `}
      positionFixed
      placement="top"
      renderTooltipContent={RedactedTooltipContent}
      children={({ onMouseEnter, onMouseLeave, ref }) => (
        <span
          ref={ref}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={css`
            display: inline-block;
            font-weight: ${deprecatedTheme.fontWeights.primary.bold};
          `}
        >
          {string}
        </span>
      )}
    />
  )
}

function RedactedTooltipContent() {
  return (
    <div
      data-testid={maskedTooltipBodyTestID}
      className={css`
        color: ${theme.color.white} !important;
      `}
    >
      <FormattedMessage
        id="title_sensitiveData_sensitiveDataMasked"
        children={(msg) => (
          <p
            className={css`
              font-weight: ${deprecatedTheme.fontWeights.primary.bold};
              font-size: 13px;
              color: ${theme.color.white} !important;
            `}
          >
            {msg}
          </p>
        )}
      />
      <FormattedMessage
        id="detail_sensitiveData_maskedTooltipHelp"
        children={(helpTextMsg) => (
          <FormattedMessage
            id="action_sensitiveData_viewPolicy"
            children={(viewPolicyMsg) => (
              <HelpText>
                {helpTextMsg}
                <Link
                  className={tooltipLinkClass}
                  isExternal
                  href={getSensitiveDataRoute()}
                >
                  {viewPolicyMsg}
                </Link>
              </HelpText>
            )}
          />
        )}
      />
    </div>
  )
}

function HelpText({ children }: { readonly children: ReactNode }) {
  return (
    <p
      className={css`
        max-width: 275px;
        overflow-wrap: break-word;
        white-space: normal;
        color: ${theme.color.white} !important;
      `}
    >
      {children}
    </p>
  )
}

export function getSplitItems(text?: string) {
  return text ? text.split(redactedRegex) : []
}
